// ★cmd-po_1139: 汎用モーダル外殻の共通部品
// catalog #07 po-modal/sip-head/sip-body/sip-foot + #02 po-icon-btn の機械転写
// DOM契約: testidPrefix-modal(frame) / -header / -title / -x-button / [beforeBody(header直後・body前・非スクロール帯・任意slot)] / -body / -footer
// cmd-po_1247: ✕(IconButton+CloseIcon)を共通枠から全廃。閉じるは各モーダルの footer ボタンのみ
import { Modal, Box } from "@mui/material";
import { useAtomValue } from "jotai";
import { AppAtoms } from "../../lib/store";
import { PO_MODAL_SX, PO_MODAL_SX_W680, PO_MODAL_SX_W760, PO_MODAL_SX_SP } from "../../lib/poModalFrame";
import type { ReactNode } from "react";
import type { SxProps, Theme } from "@mui/material/styles";

export type PoModalWidth = "w560" | "w680" | "w760";

interface PoModalShellProps {
  open: boolean;
  /** モーダルタイトル（文字列 or ReactNode） */
  title: ReactNode;
  /** onClose 共通ハンドラ（cmd-po_1247: ✕は撤去済。footer の閉じるボタン等が使用。36呼出側が渡すため prop は残置） */
  onClose: () => void;
  /** フレーム幅。デフォルト w560 (catalog .po-modal) */
  width?: PoModalWidth;
  /** PC時の幅を px 数値または CSS 文字列で上書き（SP は無視される） */
  widthPx?: number | string;
  /** data-testid の prefix: prefix-modal(frame)/-header/-title/-x-button/-body/-footer */
  testidPrefix: string;
  /** Modal 要素の data-testid（ProductServiceStartStop/AccountPassword 用既存値維持） */
  modalTestId?: string;
  /** frame Box の data-testid（既存値維持が必要な場合。省略で `${testidPrefix}-modal`） */
  frameTestId?: string;
  /** フッターに表示する ReactNode（PoButton 等）。undefined/null の場合はフッター非描画 */
  footer?: ReactNode;
  /** sip-body の sx 上書き（PoModalShell 既定 sx にスプレッドされる） */
  bodySx?: SxProps<Theme>;
  /** header と sip-body の間（非スクロール帯）に描画する任意スロット（SipStepper 等）。
   *  未指定時は何も描画しない＝additive-only（既存呼び出しの DOM / computed style を一切変えない）。 */
  beforeBody?: ReactNode;
  children?: ReactNode;
}

export function PoModalShell({
  open,
  title,
  onClose,
  width = "w560",
  widthPx,
  testidPrefix,
  modalTestId,
  frameTestId,
  footer,
  bodySx,
  beforeBody,
  children,
}: PoModalShellProps) {
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);

  const base = { w560: PO_MODAL_SX, w680: PO_MODAL_SX_W680, w760: PO_MODAL_SX_W760 }[width];
  const frameSx = {
    ...(isSmartPhone ? PO_MODAL_SX_SP : base),
    ...(widthPx !== undefined && !isSmartPhone
      ? { width: typeof widthPx === "number" ? `${widthPx}px` : widthPx }
      : {}),
  };

  return (
    <Modal
      open={open}
      aria-labelledby={`${testidPrefix}-title`}
      {...(modalTestId ? { "data-testid": modalTestId } : {})}
    >
      {/* frame: catalog .po-modal */}
      <Box
        data-testid={frameTestId ?? `${testidPrefix}-modal`}
        sx={frameSx}
      >
        {/* sip-head: catalog .sip-head */}
        <Box
          data-testid={`${testidPrefix}-header`}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: "var(--sp-5, 20px)",
            py: "var(--sp-4, 16px)",
            borderBottom: "1px solid var(--color-border-light, #efefea)",
            flexShrink: 0,
          }}
        >
          {/* sip-head h1: catalog h1 inside .sip-head */}
          <Box
            component="h1"
            id={`${testidPrefix}-title`}
            data-testid={`${testidPrefix}-title`}
            sx={{
              m: 0,
              fontSize: "var(--fs-16, 16px)",
              fontWeight: "var(--fw-bold, 700)",
              color: "var(--color-ai, #1e3a5f)",
              fontFamily: "var(--font-heading, inherit)",
            }}
          >
            {title}
          </Box>
          {/* cmd-po_1247: ✕ボタン(po-icon-btn/CloseIcon)を撤去。閉じるは各モーダルの footer ボタンのみ */}
        </Box>
        {/* beforeBody: header 直後・sip-body 前の非スクロール帯（SipStepper 等）。
            未指定=undefined ⇒ React は何も描画しない＝DOM ノード無し＝additive-only 保証 */}
        {beforeBody}
        {/* sip-body: catalog .sip-body */}
        <Box
          data-testid={`${testidPrefix}-body`}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: "var(--sp-5, 20px)",
            py: "var(--sp-4, 16px)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--sp-4, 16px)",
            ...(bodySx ?? {}),
          }}
        >
          {children}
        </Box>
        {/* sip-foot: catalog .sip-foot（footer が undefined/null の場合は非描画） */}
        {footer !== undefined && footer !== null && (
          <Box
            data-testid={`${testidPrefix}-footer`}
            sx={{
              px: "var(--sp-5, 20px)",
              py: "var(--sp-3, 12px)",
              borderTop: "1px solid var(--color-border-light, #efefea)",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "var(--sp-3, 12px)",
            }}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Modal>
  );
}
